import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { callAIStructured, AIGatewayError } from "./ai-gateway";
import { validatePublicUrl } from "./lib/url-guard";

type JdExtract = {
  roleTitle?: string;
  company?: string;
  salaryMin?: number;
  salaryMax?: number;
  salaryCurrency?: string;
  salaryPeriod?: string;
  location?: string;
  workArrangement?: string;
};

class JdFetchError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "JdFetchError";
  }
}

class ReaderStatusError extends Error {
  status: number;

  constructor(status: number) {
    super(`reader returned ${status}`);
    this.name = "ReaderStatusError";
    this.status = status;
  }
}

type FetchedJdSource = {
  markdown: string;
  title: string | null;
  company: string | null;
  location?: string | null;
  workArrangement?: string | null;
  salaryMin?: number | null;
  salaryMax?: number | null;
  salaryCurrency?: string | null;
  salaryPeriod?: string | null;
  source: string;
};

type JsonRecord = Record<string, unknown>;

const JD_EXTRACT_TOOL = {
  name: "save_jd_meta",
  description:
    "Extract job posting metadata: role title, company, salary range, location, and work arrangement.",
  parameters: {
    type: "object",
    properties: {
      roleTitle: { type: "string", description: "The job title (e.g. 'Senior Product Designer')" },
      company: { type: "string", description: "The hiring company name" },
      salaryMin: { type: "number", description: "Minimum salary number, no symbols (e.g. 120000). Omit if not stated." },
      salaryMax: { type: "number", description: "Maximum salary number, no symbols (e.g. 160000). Omit if not stated." },
      salaryCurrency: { type: "string", description: "ISO currency code (USD, EUR, GBP, etc.). Omit if unclear." },
      salaryPeriod: {
        type: "string",
        enum: ["year", "month", "week", "day", "hour"],
        description: "Pay period for the stated salary. Default to 'year' if not specified but a salary is given.",
      },
      location: {
        type: "string",
        description: "Primary city/region (e.g. 'London, UK', 'San Francisco, CA', 'EU'). Omit if not stated.",
      },
      workArrangement: {
        type: "string",
        enum: ["Remote", "Hybrid", "On-site"],
        description: "Where the role is performed. Omit if unclear.",
      },
    },
    required: [],
    additionalProperties: false,
  },
};

/**
 * Greenhouse "job-boards" (new-style) URLs are JS-rendered, but the same data
 * is exposed via a JSON endpoint. Returns null if the URL isn't a Greenhouse
 * job-boards link or the lookup fails.
 *
 * URL shape: https://job-boards.greenhouse.io/<org>/jobs/<jobId>
 * API:       https://api.greenhouse.io/v1/boards/<org>/jobs/<jobId>?content=true
 */
async function tryFetchGreenhouseJob(
  url: URL,
): Promise<FetchedJdSource | null> {
  const host = url.hostname.toLowerCase();
  if (host !== "job-boards.greenhouse.io" && host !== "boards.greenhouse.io") return null;
  const m = url.pathname.match(/^\/([^/]+)\/jobs\/(\d+)/);
  if (!m) return null;
  const [, org, jobId] = m;
  try {
    const res = await fetch(
      `https://api.greenhouse.io/v1/boards/${encodeURIComponent(org)}/jobs/${jobId}?content=true`,
      { signal: AbortSignal.timeout(15_000) },
    );
    if (!res.ok) return null;
    const json = (await res.json()) as {
      title?: string;
      content?: string;
      company_name?: string;
    };
    if (!json.content) return null;
    // Greenhouse content is HTML-encoded HTML — decode entities, then strip tags.
    const decoded = json.content
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/&amp;/g, "&")
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&nbsp;/g, " ");
    const text = decoded
      .replace(/<\s*br\s*\/?>/gi, "\n")
      .replace(/<\/(p|div|li|h[1-6])>/gi, "\n\n")
      .replace(/<li[^>]*>/gi, "- ")
      .replace(/<[^>]+>/g, "")
      .replace(/\n{3,}/g, "\n\n")
      .trim();
    const markdown = json.title ? `# ${json.title}\n\n${text}` : text;
    return {
      markdown,
      title: json.title?.trim() || null,
      company: json.company_name?.trim() || null,
      source: "greenhouse-api",
    };
  } catch {
    return null;
  }
}

function htmlToText(html: string): string {
  return html
    .replace(/<\s*br\s*\/?>/gi, "\n")
    .replace(/<\/\s*(p|div|li|h[1-6]|section|article)>/gi, "\n\n")
    .replace(/<li[^>]*>/gi, "- ")
    .replace(/<script\b[^>]*>[\s\S]*?<\/script>/gi, "")
    .replace(/<style\b[^>]*>[\s\S]*?<\/style>/gi, "")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;|&apos;/g, "'")
    .replace(/\r/g, "")
    .replace(/[ \t]{2,}/g, " ")
    .replace(/\n[ \t]+/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function asRecord(value: unknown): JsonRecord | null {
  return value && typeof value === "object" && !Array.isArray(value) ? (value as JsonRecord) : null;
}

function asString(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

function collectJsonLdNodes(value: unknown, out: JsonRecord[] = []): JsonRecord[] {
  if (Array.isArray(value)) {
    for (const item of value) collectJsonLdNodes(item, out);
    return out;
  }
  const record = asRecord(value);
  if (!record) return out;
  out.push(record);
  collectJsonLdNodes(record["@graph"], out);
  return out;
}

function isJobPostingNode(node: JsonRecord): boolean {
  const type = node["@type"];
  return Array.isArray(type)
    ? type.some((t) => String(t).toLowerCase() === "jobposting")
    : String(type ?? "").toLowerCase() === "jobposting";
}

function locationFromJson(value: unknown): string | null {
  const first = Array.isArray(value) ? value[0] : value;
  const record = asRecord(first);
  const address = asRecord(record?.address);
  const parts = [
    asString(record?.name),
    asString(address?.addressLocality),
    asString(address?.addressRegion),
    asString(address?.addressCountry),
  ].filter(Boolean);
  return parts.length ? Array.from(new Set(parts)).join(", ") : null;
}

function salaryFromJson(value: unknown): Pick<FetchedJdSource, "salaryMin" | "salaryMax" | "salaryCurrency" | "salaryPeriod"> {
  const salary = asRecord(Array.isArray(value) ? value[0] : value);
  const val = asRecord(salary?.value);
  const single = typeof val?.value === "number" ? val.value : null;
  return {
    salaryMin: typeof val?.minValue === "number" ? val.minValue : single,
    salaryMax: typeof val?.maxValue === "number" ? val.maxValue : single,
    salaryCurrency: asString(salary?.currency),
    salaryPeriod: asString(val?.unitText)?.toLowerCase() ?? null,
  };
}

function extractJsonLdJob(html: string): FetchedJdSource | null {
  const scripts = html.match(/<script[^>]+type=["']application\/ld\+json["'][^>]*>[\s\S]*?<\/script>/gi) ?? [];
  for (const script of scripts) {
    const jsonText = script.replace(/^<script[^>]*>/i, "").replace(/<\/script>$/i, "").trim();
    try {
      const nodes = collectJsonLdNodes(JSON.parse(jsonText));
      const job = nodes.find(isJobPostingNode);
      if (!job) continue;
      const org = asRecord(job.hiringOrganization);
      const description = htmlToText(asString(job.description) ?? "");
      const title = asString(job.title);
      const company = asString(org?.name);
      const location = locationFromJson(job.jobLocation) ?? locationFromJson(job.applicantLocationRequirements);
      const salary = salaryFromJson(job.baseSalary);
      const workArrangement = String(job.jobLocationType ?? "").toUpperCase() === "TELECOMMUTE" ? "Remote" : null;
      const markdown = [title ? `# ${title}` : null, description].filter(Boolean).join("\n\n").trim();
      if (markdown.length >= 80) return { markdown, title, company, location, workArrangement, ...salary, source: "json-ld" };
    } catch {
      continue;
    }
  }
  return null;
}

function extractMetaContent(html: string, name: string): string | null {
  const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const re = new RegExp(`<meta[^>]+(?:property|name)=["']${escaped}["'][^>]+content=["']([^"']+)["'][^>]*>`, "i");
  return asString(html.match(re)?.[1]?.replace(/&quot;/g, '"').replace(/&amp;/g, "&"));
}

function extractHtmlTitle(html: string): string | null {
  return asString(extractMetaContent(html, "og:title") ?? html.match(/<title[^>]*>([\s\S]*?)<\/title>/i)?.[1]?.replace(/\s+/g, " "));
}

function extractGenericHtmlJob(html: string): FetchedJdSource | null {
  const title = extractHtmlTitle(html);
  const company = extractMetaContent(html, "og:site_name");
  const description = extractMetaContent(html, "description") ?? extractMetaContent(html, "og:description");
  const text = htmlToText(html);
  const markdown = [title ? `# ${title}` : null, description, text].filter(Boolean).join("\n\n").trim();
  return markdown.length >= 300 ? { markdown, title, company, source: "direct-html" } : null;
}

async function tryFetchDirectHtmlJob(url: URL): Promise<FetchedJdSource | null> {
  try {
    const res = await fetch(url.toString(), {
      headers: {
        Accept: "text/html,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8",
        "Accept-Language": "en-US,en;q=0.9",
        "User-Agent": "Mozilla/5.0 (compatible; JobYoda/1.0; +https://jobyoda.lovable.app)",
      },
      signal: AbortSignal.timeout(15_000),
    });
    if (!res.ok) return null;
    const contentType = res.headers.get("content-type") ?? "";
    if (!contentType.includes("text/html") && !contentType.includes("application/xhtml")) return null;
    const html = await res.text();
    const structured = extractJsonLdJob(html);
    if (structured) return structured;
    const host = url.hostname.toLowerCase();
    if (["job-boards.greenhouse.io", "boards.greenhouse.io", "jobs.lever.co", "postings.lever.co", "jobs.ashbyhq.com"].includes(host)) {
      return null;
    }
    return extractGenericHtmlJob(html);
  } catch {
    return null;
  }
}

async function tryFetchLeverJob(url: URL): Promise<FetchedJdSource | null> {
  const host = url.hostname.toLowerCase();
  if (host !== "jobs.lever.co" && host !== "postings.lever.co") return null;
  const m = url.pathname.match(/^\/([^/]+)\/([a-f0-9-]+)/i);
  if (!m) return null;
  const [, org, postingId] = m;
  try {
    const res = await fetch(`https://api.lever.co/v0/postings/${encodeURIComponent(org)}/${postingId}`, {
      signal: AbortSignal.timeout(15_000),
    });
    if (!res.ok) return null;
    const json = (await res.json()) as JsonRecord;
    const title = asString(json.text);
    const company = asString(json.company);
    const location = asString(asRecord(json.categories)?.location);
    const lists = Array.isArray(json.lists) ? json.lists : [];
    const body = lists
      .map((item) => {
        const r = asRecord(item);
        const heading = asString(r?.text);
        const content = htmlToText(asString(r?.content) ?? "");
        return [heading ? `## ${heading}` : null, content].filter(Boolean).join("\n\n");
      })
      .filter(Boolean)
      .join("\n\n");
    const description = htmlToText(asString(json.descriptionPlain) ?? asString(json.description) ?? "");
    const markdown = [title ? `# ${title}` : null, description, body].filter(Boolean).join("\n\n").trim();
    return markdown.length >= 80 ? { markdown, title, company, location, source: "lever-api" } : null;
  } catch {
    return null;
  }
}

async function tryFetchAshbyJob(url: URL): Promise<FetchedJdSource | null> {
  if (url.hostname.toLowerCase() !== "jobs.ashbyhq.com") return null;
  const m = url.pathname.match(/^\/([^/]+)\/([^/?#]+)/);
  if (!m) return null;
  const [, org, postingId] = m;
  try {
    const res = await fetch(`https://jobs.ashbyhq.com/api/non-user-graphql?op=ApiJobPosting`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ operationName: "ApiJobPosting", variables: { organizationHostedJobsPageName: org, jobPostingId: postingId }, query: "query ApiJobPosting($organizationHostedJobsPageName: String!, $jobPostingId: String!) { jobPosting(organizationHostedJobsPageName: $organizationHostedJobsPageName, jobPostingId: $jobPostingId) { title descriptionHtml departmentName locationName employmentType workplaceType } }" }),
      signal: AbortSignal.timeout(15_000),
    });
    if (!res.ok) return null;
    const root = asRecord(await res.json());
    const posting = asRecord(asRecord(root?.data)?.jobPosting);
    if (!posting) return null;
    const title = asString(posting.title);
    const location = asString(posting.locationName);
    const workArrangement = asString(posting.workplaceType);
    const description = htmlToText(asString(posting.descriptionHtml) ?? "");
    const markdown = [title ? `# ${title}` : null, description].filter(Boolean).join("\n\n").trim();
    return markdown.length >= 80 ? { markdown, title, company: org, location, workArrangement, source: "ashby-api" } : null;
  } catch {
    return null;
  }
}

async function fetchViaJina(url: string, useBrowser: boolean): Promise<string> {
  // CRITICAL: encode the target URL so its own query string ('?source=...') is
  // not interpreted as r.jina.ai's query string.
  const encoded = encodeURIComponent(url);
  const headers: Record<string, string> = {
    Accept: "text/plain",
    "X-Return-Format": "markdown",
  };
  if (useBrowser) headers["X-Engine"] = "browser";
  const res = await fetch(`https://r.jina.ai/${encoded}`, {
    headers,
    signal: AbortSignal.timeout(useBrowser ? 30_000 : 20_000),
  });
  if (!res.ok) {
    throw new ReaderStatusError(res.status);
  }
  return (await res.text()).trim();
}

async function fetchJdMarkdown(url: string): Promise<string> {
  // First attempt: fast static reader.
  let lastErr: unknown = null;
  let lastReaderStatus: number | null = null;
  try {
    const md = await fetchViaJina(url, false);
    if (md.length >= 80) return md;
    lastErr = new Error("page returned no readable content");
  } catch (e) {
    lastErr = e;
    if (e instanceof ReaderStatusError) lastReaderStatus = e.status;
  }
  // Retry with browser rendering for SPA boards (Greenhouse, Lever, Ashby, Workday).
  try {
    const md = await fetchViaJina(url, true);
    if (md.length >= 80) return md;
  } catch (e) {
    lastErr = e;
    if (e instanceof ReaderStatusError) lastReaderStatus = e.status;
  }
  if (lastReaderStatus === 429) {
    throw new JdFetchError(
      "The reader service is rate-limited, and direct/API extraction did not find a readable JD for this site.",
    );
  }
  throw new JdFetchError(
    `Could not fetch that page (${lastErr instanceof Error ? lastErr.message : "unknown error"}). Try pasting the JD text instead.`,
  );
}

// Anchors that typically mark the start of the actual JD body across job boards.
const JD_BODY_ANCHORS = [
  /^#{1,3}\s*description\b/im,
  /^#{1,3}\s*about (?:the|this) (?:role|job|position|opportunity)\b/im,
  /^#{1,3}\s*the role\b/im,
  /^#{1,3}\s*responsibilities\b/im,
  /^#{1,3}\s*what you['’]ll do\b/im,
  /^#{1,3}\s*what you will do\b/im,
  /^#{1,3}\s*role overview\b/im,
  /^#{1,3}\s*job (?:summary|description)\b/im,
  /^experience\/?skills required\b/im,
  /^minimum qualifications\b/im,
  /^basic qualifications\b/im,
  /^who you are\b/im,
];

const BOILERPLATE_LINE_PATTERNS = [
  /^title:\s*/i,
  /^url source:\s*/i,
  /^markdown content:\s*/i,
  /^warning:\s*/i,
  /^!\[/, // markdown images
  /^\s*\[skip to main content\]/i,
  /^\s*\[?(facebook|twitter|linkedin|share|open sharing options)\b/i,
  /^\s*(accept all|essential only|manage preferences|we care about your privacy)\b/i,
  /^\s*(cookie|cookies)\b/i,
  /^\s*\[?(sign in|log in|register|create account|saved jobs|search jobs)\b/i,
];

const UNAVAILABLE_PAGE_PATTERNS = [
  /warning:\s*target url returned error\s*(?:404|410|403|401)/i,
  /^#\s*(?:page not found|404|not found)\b/im,
  /\bpage not found\s*\|\s*[^\n]+careers/i,
  /\b(?:this job|this position|this posting)\s+(?:is\s+)?(?:no longer available|closed|expired|removed|not available)/i,
  /\bjob\s+(?:no longer available|not found|closed|expired|removed)\b/i,
];

const JOB_SIGNAL_PATTERNS = [
  /\b(responsibilities|what you['’]?ll do|what you will do|your impact)\b/i,
  /\b(qualifications|requirements|experience\/skills|required skills|minimum qualifications|basic qualifications)\b/i,
  /\b(compensation|salary|benefits|pay range|base pay)\b/i,
  /\b(location|remote|hybrid|on-site|onsite)\b/i,
  /\b(apply|candidate|employment|recruiting process)\b/i,
];

function isUnavailablePage(raw: string): boolean {
  return UNAVAILABLE_PAGE_PATTERNS.some((re) => re.test(raw));
}

function isSalesforceCareersJob(url: URL): boolean {
  return url.hostname.toLowerCase() === "careers.salesforce.com" && /\/jobs\//i.test(url.pathname);
}

function cleanSalesforceJdBody(raw: string): string {
  let text = raw.replace(/^Title:.*?Markdown Content:\s*/is, "").trim();
  const heading = text.match(/^#\s+([^\n]+)$/m);
  if (heading?.index != null) text = text.slice(heading.index);

  const firstBodyAnchor = [
    /\n(?:We(?:'|’)re|We are)\s+/i,
    /\nTo be successful in this role/i,
    /\n\*\*About Salesforce UX\*\*/i,
    /\nResponsibilities\s*\n/i,
  ]
    .map((re) => {
      const m = text.match(re);
      return m?.index ?? -1;
    })
    .filter((idx) => idx > 0)
    .sort((a, b) => a - b)[0];

  const titleLine = text.match(/^#\s+([^\n]+)$/m)?.[0] ?? "";
  if (firstBodyAnchor && firstBodyAnchor > 0) {
    text = `${titleLine}\n\n${text.slice(firstBodyAnchor).trim()}`.trim();
  }

  const end = text.search(/\n#{2,3}\s*(?:At Salesforce, we strive|Unleash Your Potential)\b/i);
  if (end > 600) text = text.slice(0, end).trim();
  return text;
}

function hasEnoughJobSignals(text: string): boolean {
  const signalCount = JOB_SIGNAL_PATTERNS.reduce((count, re) => count + (re.test(text) ? 1 : 0), 0);
  const hasHeading = /^#\s+.+/m.test(text) || /^.+\s+\|\s+.+careers/im.test(text);
  return signalCount >= 2 || (text.length >= 1_200 && hasHeading && signalCount >= 1);
}

function cleanJdBody(raw: string): string {
  let text = raw;
  // Trim leading reader/nav noise by locating the first JD anchor.
  let anchorIdx = -1;
  for (const re of JD_BODY_ANCHORS) {
    const m = text.match(re);
    if (m && m.index != null && (anchorIdx === -1 || m.index < anchorIdx)) {
      anchorIdx = m.index;
    }
  }
  if (anchorIdx > 200) {
    text = text.slice(anchorIdx);
  }

  // Drop obvious boilerplate lines.
  const cleaned = text
    .split("\n")
    .filter((line) => !BOILERPLATE_LINE_PATTERNS.some((re) => re.test(line)))
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

  return cleaned;
}

function fallbackTitleFromMarkdown(md: string): string | null {
  // Look for first H1/H2
  const h1 = md.match(/^#\s+(.+)$/m);
  if (h1) return h1[1].trim();
  const h2 = md.match(/^##\s+(.+)$/m);
  if (h2) return h2[1].trim();
  return null;
}

// Strip tracking junk that often breaks readers and leaks into stored URLs.
const TRACKING_PARAMS = new Set([
  "source",
  "ref",
  "referrer",
  "gh_src",
  "gh_jid",
  "lever-source",
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "fbclid",
  "gclid",
  "mc_cid",
  "mc_eid",
]);

function cleanJobUrl(u: URL): URL {
  const out = new URL(u.toString());
  for (const key of Array.from(out.searchParams.keys())) {
    if (TRACKING_PARAMS.has(key.toLowerCase())) out.searchParams.delete(key);
  }
  return out;
}

export const fetchJdFromUrl = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { url: string }) => {
    const u = validatePublicUrl(input?.url ?? "");
    return { url: cleanJobUrl(u).toString() };
  })
  .handler(async ({ data }) => {
    const url = new URL(data.url);
    const host = url.hostname;

    let raw: string;
    let presetTitle: string | null = null;
    let presetCompany: string | null = null;
    let presetLocation: string | null = null;
    let presetWorkArrangement: string | null = null;
    let presetSalaryMin: number | null = null;
    let presetSalaryMax: number | null = null;
    let presetSalaryCurrency: string | null = null;
    let presetSalaryPeriod: string | null = null;

    const direct = await tryFetchDirectHtmlJob(url);
    const board = direct ?? (await tryFetchGreenhouseJob(url)) ?? (await tryFetchLeverJob(url)) ?? (await tryFetchAshbyJob(url));
    if (board) {
      raw = board.markdown;
      presetTitle = board.title;
      presetCompany = board.company;
      presetLocation = board.location ?? null;
      presetWorkArrangement = board.workArrangement ?? null;
      presetSalaryMin = board.salaryMin ?? null;
      presetSalaryMax = board.salaryMax ?? null;
      presetSalaryCurrency = board.salaryCurrency ?? null;
      presetSalaryPeriod = board.salaryPeriod ?? null;
      console.log(`[fetchJdFromUrl] host=${host} source=${board.source} rawLen=${raw.length}`);
    } else {
      raw = await fetchJdMarkdown(data.url);
      console.log(`[fetchJdFromUrl] host=${host} source=jina-reader rawLen=${raw.length}`);
    }

    if (raw.length < 80) {
      console.warn(`[fetchJdFromUrl] host=${host} reject=raw-too-short len=${raw.length}`);
      throw new JdFetchError(
        `That page returned almost no content (${raw.length} chars). The site may block server-side fetches. Paste the JD text instead.`,
      );
    }

    if (isUnavailablePage(raw)) {
      console.warn(`[fetchJdFromUrl] host=${host} reject=unavailable-page`);
      throw new JdFetchError(
        "That job page appears to be unavailable, expired, or blocked. Paste the JD text instead.",
      );
    }

    // Cap to keep tokens bounded — most JDs fit in 20k chars.
    const rawCapped = raw.slice(0, 20_000);
    const cleaned = isSalesforceCareersJob(url)
      ? cleanJdBody(cleanSalesforceJdBody(rawCapped))
      : cleanJdBody(rawCapped);
    // Prefer cleaned text when it's substantial; otherwise fall back to the
    // raw capped markdown so the user always sees what was fetched.
    const jdText = cleaned.length >= 200 ? cleaned : rawCapped;
    console.log(
      `[fetchJdFromUrl] host=${host} rawCapped=${rawCapped.length} cleaned=${cleaned.length} final=${jdText.length}`,
    );

    if (jdText.length < 80) {
      console.warn(`[fetchJdFromUrl] host=${host} reject=final-too-short len=${jdText.length}`);
      throw new JdFetchError(
        "That page didn't contain a readable job description (mostly navigation or cookie text). Try pasting the JD text instead.",
      );
    }

    if (!hasEnoughJobSignals(jdText)) {
      console.warn(`[fetchJdFromUrl] host=${host} reject=weak-job-signals len=${jdText.length}`);
      throw new JdFetchError(
        "That page did not contain enough readable job-description content. Paste the JD text instead.",
      );
    }

    // Try LLM extraction for metadata (always run — Greenhouse gives us title/company
    // but not salary/location, so we still benefit from LLM extraction).
    let extract: JdExtract = {};
    try {
      extract = await callAIStructured<JdExtract>({
        messages: [
          {
            role: "system",
            content:
              "Extract job posting metadata from the page. Return canonical role title and company name (e.g. 'Senior Product Designer', 'Stripe'). Extract salary range as numbers only (no symbols/commas) along with currency and pay period. Extract the primary work location (city, region or country) and the work arrangement (Remote, Hybrid, or On-site). Omit any field you can't determine with confidence — do NOT guess.",
          },
          {
            role: "user",
            content: `Source URL: ${data.url}\n\nPage content (markdown):\n${jdText.slice(0, 8000)}`,
          },
        ],
        tool: JD_EXTRACT_TOOL,
      });
    } catch (e) {
      // Non-fatal — we'll still return jd_text.
      if (e instanceof AIGatewayError) {
        console.warn("JD extract LLM failed:", e.message);
      } else {
        console.warn("JD extract failed:", e);
      }
    }

    const roleTitle =
      presetTitle || extract.roleTitle?.trim() || fallbackTitleFromMarkdown(jdText) || null;
    const company = presetCompany || extract.company?.trim() || null;

    const allowedArrangements = new Set(["Remote", "Hybrid", "On-site"]);
    const workArrangement =
      presetWorkArrangement && allowedArrangements.has(presetWorkArrangement)
        ? presetWorkArrangement
        : extract.workArrangement && allowedArrangements.has(extract.workArrangement)
        ? extract.workArrangement
        : null;

    return {
      jd_text: jdText,
      jdText, // camelCase alias to survive any RPC key-casing layer
      char_count: jdText.length,
      role_title: roleTitle,
      company,
      source_url: data.url,
      salary_min: presetSalaryMin ?? (typeof extract.salaryMin === "number" ? extract.salaryMin : null),
      salary_max: presetSalaryMax ?? (typeof extract.salaryMax === "number" ? extract.salaryMax : null),
      salary_currency: presetSalaryCurrency?.trim()?.toUpperCase() || extract.salaryCurrency?.trim()?.toUpperCase() || null,
      salary_period: presetSalaryPeriod?.trim()?.toLowerCase() || extract.salaryPeriod?.trim()?.toLowerCase() || null,
      location: presetLocation || extract.location?.trim() || null,
      work_arrangement: workArrangement,
    };
  });

